// src/features/course2faSlice.js
// The identity re-challenge (MTO 1.2.1). The server decides when it is due
// and which method it uses (question | totp | otp) — the client only shows
// it (B-4). Requests that met 401 COURSE_2FA_REQUIRED wait in a non-Redux
// map and are replayed after a successful answer, or rejected if the student
// gives up and contacts support.
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import fetchWithAuth from "../utils/fetchWithAuth";

export const __pendingRequestMap = new Map();

function replayAll() {
  const entries = [...__pendingRequestMap.values()];
  __pendingRequestMap.clear();
  entries.forEach(({ url, options, resolve, reject }) =>
    fetchWithAuth(url, options).then(resolve).catch(reject)
  );
}

/** Reject every suspended request (student gave up, or the account locked). */
export function rejectPendingChallenges(code = "IDENTITY_NOT_VERIFIED") {
  const entries = [...__pendingRequestMap.values()];
  __pendingRequestMap.clear();
  entries.forEach(({ reject }) => reject(new Error(code)));
}

export const verifyCourse2fa = createAsyncThunk(
  "course2fa/verify",
  async ({ token = "", answer = "", code = "" }, { rejectWithValue, dispatch }) => {
    try {
      await fetchWithAuth("/api/2fa/course-verify", {
        method: "POST",
        body: JSON.stringify({
          ...(answer ? { answer } : {}),
          ...(token ? { token } : {}),
          ...(code ? { code } : {})
        })
      });
      dispatch(course2faSlice.actions.clear());
      replayAll();
      return true;
    } catch (err) {
      return rejectWithValue(err.body || { code: err.code || err.message });
    }
  }
);

const initialState = {
  required: false,
  method: "totp", // "question" | "totp" | "otp" — set from the server's challenge
  questionIndex: null,
  questionText: "",
  destination: null,
  requestKey: null,
  loading: false,
  error: null,
  attemptsRemaining: null,
  helpShown: false
};

function applyChallenge(state, challenge) {
  if (!challenge) return;
  state.method = challenge.method || state.method;
  state.questionIndex = challenge.questionIndex ?? null;
  state.questionText = challenge.question || "";
  state.destination = challenge.destination || null;
}

const course2faSlice = createSlice({
  name: "course2fa",
  initialState,
  reducers: {
    /** fetchWithAuth met 401 COURSE_2FA_REQUIRED (payload carries the server's challenge). */
    prompt(state, action) {
      state.required = true;
      state.requestKey = action.payload.requestKey || state.requestKey;
      state.error = null;
      if (action.payload.challenge) applyChallenge(state, action.payload.challenge);
      else if (!state.method) state.method = "totp";
    },
    /** SessionGuard saw challengeDue in /api/session/status. */
    showChallenge(state, action) {
      state.required = true;
      state.error = null;
      applyChallenge(state, action.payload);
    },
    promptQuestion(state, action) {
      const { requestKey, questionIndex, questionText = "" } = action.payload;
      state.required = true;
      state.method = "question";
      state.requestKey = requestKey;
      state.questionIndex = questionIndex;
      state.questionText = questionText;
      state.error = null;
    },
    showHelp(state, action) {
      state.helpShown = action.payload !== false;
    },
    clear: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(verifyCourse2fa.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyCourse2fa.fulfilled, () => initialState)
      .addCase(verifyCourse2fa.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.code || "VERIFY_FAILED";
        state.attemptsRemaining = action.payload?.attemptsRemaining ?? null;
        if (action.payload?.challenge) applyChallenge(state, action.payload.challenge);
      });
  }
});

export const { prompt, showChallenge, promptQuestion, showHelp, clear } = course2faSlice.actions;
export default course2faSlice.reducer;
